import { useEffect, useState } from "react";
import axios from "axios";
import { X, Upload, Camera, Loader2, Play } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gallery`);
      setPhotos(res.data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section
      id="galerie"
      data-testid="section-gallery"
      className="py-24 md:py-36 bg-white"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="text-sage-500 uppercase tracking-[0.35em] text-xs mb-6">· Erinnerungen ·</p>
          <h2
            data-testid="gallery-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-sage-800"
          >
            Foto<span className="font-script text-sage-500 text-5xl sm:text-6xl lg:text-7xl">galerie</span>
          </h2>
          <p className="mt-6 text-sage-600 max-w-xl mx-auto">
            Eure schönsten Momente an unserem Tag — teilt eure Fotos und Videos direkt mit uns.
          </p>
          <button
            data-testid="gallery-upload-cta"
            onClick={() => setShowUpload(true)}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 text-white px-7 py-3 text-sm uppercase tracking-[0.25em] transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-lg"
          >
            <Camera size={16} />
            Foto oder Video teilen
          </button>
        </div>

        {loading && photos.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-sage-400">
            <Loader2 className="animate-spin" />
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-16 text-sage-500 bg-sage-50 rounded-2xl border border-sage-200">
            <Camera className="mx-auto mb-4 text-sage-300" size={32} />
            <p>Noch keine Fotos. Sei der/die Erste! 📸</p>
          </div>
        ) : (
          <div
            data-testid="gallery-grid"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5"
          >
            {photos.map((p, idx) => {
              const isVideo = p.kind === "video";
              return (
                <button
                  key={p.name}
                  data-testid={`gallery-item-${idx}`}
                  onClick={() => setLightbox(p)}
                  className={`group relative overflow-hidden rounded-xl ${
                    idx % 5 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"
                  } bg-sage-100`}
                >
                  {isVideo ? (
                    <>
                      <video
                        src={p.full_url}
                        preload="metadata"
                        muted
                        playsInline
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-sage-950/25 group-hover:bg-sage-950/10 transition-colors">
                        <span className="w-14 h-14 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow-lg">
                          <Play className="text-sage-700 translate-x-0.5" size={22} fill="currentColor" />
                        </span>
                      </div>
                    </>
                  ) : (
                    <img
                      src={p.thumb_url}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-sage-950/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-sage-950/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 backdrop-blur"
            onClick={() => setLightbox(null)}
            aria-label="Schließen"
          >
            <X size={24} />
          </button>
          {lightbox.kind === "video" ? (
            <video
              src={lightbox.full_url}
              controls
              autoPlay
              playsInline
              className="max-h-[90vh] max-w-[95vw] rounded-xl shadow-2xl bg-black"
            />
          ) : (
            <img
              src={lightbox.full_url}
              alt={lightbox.name}
              className="max-h-[90vh] max-w-[95vw] object-contain rounded-xl shadow-2xl"
            />
          )}
        </div>
      )}

      {showUpload && (
        <UploadDialog onClose={() => setShowUpload(false)} onUploaded={load} />
      )}
    </section>
  );
};

const UploadDialog = ({ onClose, onUploaded }) => {
  const [name, setName] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, currentPct: 0, currentName: "" });

  const onPick = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(list);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error("Bitte mindestens eine Datei auswählen");
      return;
    }
    setUploading(true);
    setProgress({ done: 0, total: files.length, currentPct: 0, currentName: "" });
    let success = 0;
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress((p) => ({ ...p, currentName: f.name, currentPct: 0 }));
      try {
        const fd = new FormData();
        fd.append("file", f);
        if (name) fd.append("uploader_name", name);
        await axios.post(`${API}/gallery/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setProgress((p) => ({ ...p, currentPct: pct }));
          },
        });
        success++;
      } catch (err) {
        failed++;
        console.error("upload failed", f.name, err);
        const detail = err?.response?.data?.detail;
        if (detail) toast.error(`${f.name}: ${detail}`);
      }
      setProgress((p) => ({ ...p, done: i + 1, currentPct: 100 }));
    }
    setUploading(false);
    if (success > 0) toast.success(`${success} Datei${success > 1 ? "en" : ""} hochgeladen — Danke! 💚`);
    if (failed > 0 && success === 0) toast.error(`${failed} Datei${failed > 1 ? "en" : ""} konnten nicht hochgeladen werden`);
    if (success > 0) {
      await onUploaded();
      onClose();
    }
  };

  return (
    <div
      data-testid="gallery-upload-dialog"
      className="fixed inset-0 z-50 bg-sage-950/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-white rounded-t-3xl md:rounded-2xl shadow-2xl border border-sage-200"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-sage-100">
          <div>
            <h3 className="font-serif text-2xl text-sage-800">Fotos &amp; Videos teilen</h3>
            <p className="text-xs text-sage-500">
              Lade Bilder oder kurze Videos direkt in unsere Galerie.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sage-400 hover:text-sage-700 p-2"
            aria-label="Schließen"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.2em] text-sage-600 mb-2">
              Dein Name (optional)
            </span>
            <input
              data-testid="gallery-uploader-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Anna"
              className="w-full rounded-lg border border-sage-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
            />
          </label>

          <label
            data-testid="gallery-upload-file-label"
            className="flex flex-col items-center justify-center gap-3 w-full rounded-2xl border-2 border-dashed border-sage-300 bg-sage-50 px-4 py-10 cursor-pointer hover:border-sage-400 hover:bg-sage-100/50 transition-colors"
          >
            <Upload className="text-sage-500" size={32} />
            <div className="text-center">
              <div className="text-sage-700 text-sm font-medium">
                {files.length === 0
                  ? "Fotos oder Videos auswählen"
                  : `${files.length} Datei${files.length > 1 ? "en" : ""} ausgewählt`}
              </div>
              <div className="text-xs text-sage-500 mt-1">
                Bilder max. 12 MB · Videos max. 100 MB
              </div>
            </div>
            <input
              data-testid="gallery-upload-file-input"
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={onPick}
              className="hidden"
            />
          </label>

          {uploading && (
            <div className="text-center text-sm text-sage-600 space-y-2">
              <div>
                <Loader2 className="inline-block animate-spin mr-2" size={14} />
                Datei {progress.done + (progress.currentPct < 100 ? 1 : 0)} von {progress.total}
                {progress.currentName ? ` · ${progress.currentName}` : ""}
              </div>
              <div className="w-full h-2 rounded-full bg-sage-100 overflow-hidden">
                <div
                  className="h-full bg-sage-500 transition-all duration-200"
                  style={{ width: `${progress.currentPct}%` }}
                />
              </div>
              <div className="text-xs text-sage-500">{progress.currentPct}%</div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-sage-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="rounded-full border border-sage-300 text-sage-700 hover:bg-sage-50 px-5 py-2.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            data-testid="gallery-upload-submit"
            type="submit"
            disabled={uploading || files.length === 0}
            className="rounded-full bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-6 py-2.5 text-sm uppercase tracking-wide flex items-center gap-2"
          >
            <Upload size={14} />
            {uploading ? "Lädt..." : "Hochladen"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Gallery;
